/*
 * ------------------------------------------------------------
 * Compre junto do carrinho (trilho do drawer e faixa da página /cart).
 *
 * Fonte: Section Rendering API sobre o endpoint de recomendações
 *   /recommendations/products?product_id=<âncora>&limit=N
 *     &section_id=cart-drawer-upsell&intent=related
 * que devolve HTML pronto, não JSON.
 *
 * Otimização portada do tema Cadiveu (refactor/cart-drawer-perf): o fetch só
 * acontece quando o PRIMEIRO item do carrinho muda. Mexer em quantidade,
 * remover outro item ou aplicar cupom não gera rede.
 *
 * Adaptação ao Dawn: lá o DOM do drawer persistia entre mutações e o componente
 * precisava observar o estado do carrinho para descobrir a âncora. Aqui o DOM é
 * recriado por innerHTML a cada mutação, e o Liquid reentrega a âncora em
 * data-anchor-id — então o cache mora em module scope, o componente não assina
 * evento nenhum, e com cache quente a injeção é síncrona dentro do
 * connectedCallback: sem flash e sem requisição.
 *
 * O Cadiveu usava um contador global de token contra respostas fora de ordem.
 * Aqui isso não serve: na página /cart o drawer e a faixa coexistem, e um
 * contador compartilhado faria uma instância invalidar a outra. Cada elemento
 * dispara no máximo um fetch, então o guard correto é isConnected — um elemento
 * já descartado por um re-render não pinta nada.
 * ------------------------------------------------------------
 */

const upsellCache = { anchorId: null, html: null };

/* Deduplica o fetch quando as duas instâncias sobem juntas na página /cart. */
const upsellPending = { anchorId: null, promise: null };

function fetchUpsellHtml(element, anchorId) {
  if (upsellCache.anchorId === anchorId && upsellCache.html !== null) {
    return Promise.resolve(upsellCache.html);
  }
  if (upsellPending.anchorId === anchorId && upsellPending.promise) {
    return upsellPending.promise;
  }

  const params = new URLSearchParams({
    product_id: anchorId,
    limit: element.dataset.limit || '10',
    section_id: element.dataset.sectionId || 'cart-drawer-upsell',
    intent: 'related',
  });
  const baseUrl = element.dataset.url || '/recommendations/products';

  const promise = fetch(`${baseUrl}?${params}`)
    .then((response) => {
      if (!response.ok) throw new Error(`recommendations respondeu ${response.status}`);
      return response.text();
    })
    .then((text) => {
      const list = new DOMParser().parseFromString(text, 'text/html').querySelector('cart-upsell-list');
      const html = list ? list.innerHTML : '';

      upsellCache.anchorId = anchorId;
      upsellCache.html = html;
      return html;
    })
    .finally(() => {
      if (upsellPending.anchorId === anchorId) {
        upsellPending.anchorId = null;
        upsellPending.promise = null;
      }
    });

  upsellPending.anchorId = anchorId;
  upsellPending.promise = promise;
  return promise;
}

class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.onChange = this.handleVariantChange.bind(this);
    this.addEventListener('change', this.onChange);

    const anchorId = this.dataset.anchorId || '';
    if (!anchorId) {
      this.innerHTML = '';
      return;
    }

    /* Cache quente: injeção síncrona, ainda dentro da atribuição de innerHTML
       que recriou o drawer. Não há repintura intermediária. */
    if (upsellCache.anchorId === anchorId && upsellCache.html !== null) {
      this.inject(upsellCache.html);
      return;
    }

    this.load(anchorId);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  async load(anchorId) {
    try {
      const html = await fetchUpsellHtml(this, anchorId);
      /* Um re-render do carrinho já descartou este elemento: quem pinta é a
         instância nova, que encontra o cache pronto. */
      if (!this.isConnected || this.dataset.anchorId !== anchorId) return;
      this.inject(html);
    } catch (error) {
      console.error('Erro ao carregar o compre junto:', error);
    }
  }

  inject(html) {
    this.innerHTML = html;
    this.filterItemsInCart();
    this.tagOrigin();
  }

  tagOrigin() {
    const origem = this.dataset.origem;
    if (!origem) return;
    this.querySelectorAll('input[name="properties[_origem]"]').forEach((input) => {
      input.value = origem;
    });
  }

  /* O cache guarda a lista sem filtro, então o filtro roda a cada injeção
     contra os ids que o Liquid acabou de reentregar. É o que faz o card sumir
     sozinho depois que o cliente adiciona aquele produto. */
  filterItemsInCart() {
    const ids = (this.dataset.cartProductIds || '').split(',').filter(Boolean);
    if (ids.length) {
      this.querySelectorAll('.cart-upsell__card').forEach((card) => {
        if (ids.includes(card.dataset.productId)) card.remove();
      });
    }

    if (!this.querySelector('.cart-upsell__card')) this.innerHTML = '';
  }

  handleVariantChange(event) {
    const select = event.target.closest('.cart-upsell__variants');
    if (!select) return;

    const option = select.selectedOptions[0];
    const card = select.closest('.cart-upsell__card');
    if (!option || !card) return;

    const priceNode = card.querySelector('[data-upsell-price]');
    if (priceNode && option.dataset.price) priceNode.textContent = option.dataset.price;

    /* O Liquid só preenche data-discount quando compare_at_price > price, então
       a presença do atributo é o próprio sinal de promoção. */
    const onSale = Boolean(option.dataset.discount);

    const compareNode = card.querySelector('[data-upsell-compare]');
    if (compareNode) {
      compareNode.textContent = onSale ? option.dataset.comparePrice : '';
      compareNode.hidden = !onSale;
    }

    const badgeNode = card.querySelector('[data-upsell-badge]');
    if (badgeNode) {
      badgeNode.textContent = onSale ? `-${option.dataset.discount}%` : '';
      badgeNode.hidden = !onSale;
    }
  }
}

customElements.define('cart-upsell', CartUpsell);
